import { reviveDates } from './chunk-SUNYEKTQ.js';
import './chunk-WH2C42V5.js';
import { ApiError } from './chunk-LEGW4QDU.js';
export { ApiError } from './chunk-LEGW4QDU.js';
import { resolveTranslator } from './chunk-RRFUPILC.js';
import './chunk-4DPTK2CR.js';
import toast from 'react-hot-toast';
import { QueryClient } from '@tanstack/react-query';

// src/internal/api/createApiClient.ts
var MUTATING = /* @__PURE__ */ new Set(["POST", "PUT", "PATCH", "DELETE"]);
function readCookie(name) {
  if (typeof document === "undefined") return "";
  return document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] ?? "";
}
function createApiClient(opts) {
  const baseUrl = opts.baseUrl.replace(/\/+$/, "");
  const fetchImpl = opts.fetchImpl ?? fetch;
  const csrfCookieName = opts.csrfCookieName === null ? null : opts.csrfCookieName ?? "adminer_csrf";
  const shouldReviveDates = opts.reviveDates ?? true;
  async function request(method, path, body) {
    const headers = {
      "Content-Type": "application/json"
    };
    if (csrfCookieName && MUTATING.has(method)) {
      const tok = readCookie(csrfCookieName);
      if (tok) headers["X-CSRF-Token"] = tok;
    }
    const res = await fetchImpl(`${baseUrl}${path}`, {
      method,
      credentials: "include",
      headers,
      body: body !== void 0 ? JSON.stringify(body) : void 0
    });
    if (!res.ok) {
      let parsed;
      try {
        parsed = await res.json();
      } catch {
        parsed = null;
      }
      throw new ApiError(res.status, parsed);
    }
    if (res.status === 204) return void 0;
    const data = await res.json();
    return shouldReviveDates ? reviveDates(data) : data;
  }
  return {
    baseUrl,
    request,
    get: (path) => request("GET", path),
    post: (path, body) => request("POST", path, body),
    put: (path, body) => request("PUT", path, body),
    patch: (path, body) => request("PATCH", path, body),
    delete: (path) => request("DELETE", path)
  };
}
var configuredTranslator;
function configureNotify(opts) {
  configuredTranslator = opts.t;
}
function notifySuccess(message) {
  toast.success(message);
}
function notifyInfo(message) {
  toast(message);
}
function notifyError(error, opts = {}) {
  const t = opts.t ?? configuredTranslator ?? resolveTranslator(void 0);
  toast.error(extractErrorMessage(error, t));
}
function extractErrorMessage(error, t) {
  if (error instanceof ApiError) {
    const body = error.body;
    if (body && typeof body === "object") {
      const b = body;
      if (typeof b.message === "string" && b.message) return b.message;
      if (typeof b.error === "string" && b.error) return b.error;
    }
    if (error.status === 401) return t("apiError.401");
    if (error.status === 403) return t("apiError.403");
    if (error.status === 404) return t("apiError.404");
    if (error.status >= 500) return t("apiError.server");
    return t("apiError.generic", { status: error.status });
  }
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return t("apiError.unexpected");
}
function createDefaultQueryClient(opts = {}) {
  const maxRetries = opts.maxRetries ?? 1;
  const staleTime = opts.staleTimeMs ?? 3e4;
  let unauthorizedFired = false;
  return new QueryClient({
    defaultOptions: {
      ...opts.defaults ?? {},
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status === 401) {
            if (!unauthorizedFired) {
              unauthorizedFired = true;
              opts.onUnauthorized?.();
            }
            return false;
          }
          return failureCount < maxRetries;
        },
        refetchOnWindowFocus: true,
        staleTime,
        ...opts.defaults?.queries ?? {}
      }
    }
  });
}

export { configureNotify, createApiClient, createDefaultQueryClient, notifyError, notifyInfo, notifySuccess };
//# sourceMappingURL=api.js.map
//# sourceMappingURL=api.js.map